import { t } from "i18next";
import { Controller, useFormContext } from "react-hook-form";
import { Input } from "ui";

import {
  ProductFormValues,
  VariantBlockProps
} from "../../ProductDetailsForm.types";
import { VariantBadgeBlock } from "./VariantBadgeBlock";
import { VariantBarcodesBlock } from "./VariantBarcodesBlock";
import { VariantBoughtTogetherBlock } from "./VariantBoughtTogetherBlock";
import { VariantBulkDiscountsBlock } from "./VariantBulkDiscountsBlock";
import { VariantGifBlock } from "./VariantGifBlock";
import { VariantImagesBlock } from "./VariantImagesBlock";
import { VariantPricesBlock } from "./VariantPricesBlock";

import styles from "../../ProductDetailsForm.module.scss";

const VariantBlock = ({
  index,
  colorLabel,
  sku,
  shortId,
  defaultValues
}: VariantBlockProps) => {
  const { control, clearErrors } = useFormContext<ProductFormValues>();

  return (
    <div className={styles.productForm__block}>
      <h4 className={styles.productForm__block__title}>
        {`${t(colorLabel)} color`}
        <span className={styles.productForm__skuLabel}>SKU: {sku}</span>
        <span className={styles.productForm__skuLabel}>
          {t("Short ID")}: {shortId}
        </span>
      </h4>

      <Controller
        control={control}
        name={`variants.${index}.name`}
        defaultValue={""}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Input
            value={value ?? ""}
            onChange={onChange}
            placeholder={t("Enter product name")}
            label={t("Product Name")}
            errorMessage={error?.message}
            maxLength={50}
          />
        )}
      />

      <Controller
        control={control}
        name={`variants.${index}.shortTitle`}
        defaultValue={""}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Input
            value={value ?? ""}
            onChange={onChange}
            placeholder={t("Leave empty to use the full name")}
            label={t("Short title (WhatsApp card)")}
            errorMessage={error?.message}
            maxLength={25}
          />
        )}
      />

      <div className={styles.productForm__barcodesAndImagesContainer}>
        <VariantBarcodesBlock
          variantIndex={index}
          defaultValuesBarcodes={defaultValues.variants[index]?.barcodes}
        />

        <div className={styles.productForm__verticalSeparatorLine} />

        <div className={styles.productForm__imagesAndMiniatureContainer}>
          <VariantImagesBlock variantIndex={index} />
          <VariantGifBlock variantIndex={index} />
        </div>
      </div>

      <div className={styles.productForm__block__selectors}>
        <VariantBadgeBlock variantIndex={index} />

        <Controller
          control={control}
          name={`variants.${index}.weight`}
          defaultValue={null}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              value={value ?? ""}
              type="number"
              onChange={(e) => {
                onChange(e);
                clearErrors(`variants.${index}.weight`);
              }}
              placeholder={t("Enter weight")}
              label={t("Weight (kg)")}
              errorMessage={error?.message}
              maxLength={6}
            />
          )}
        />

        <Controller
          control={control}
          name={`variants.${index}.volume`}
          defaultValue={null}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              value={value ?? ""}
              type="number"
              onChange={(e) => {
                clearErrors(`variants.${index}.volume`);
                onChange(e);
              }}
              placeholder={t("Enter volume")}
              label={t("Volume (cbm)")}
              errorMessage={error?.message}
              maxLength={6}
            />
          )}
        />
      </div>

      <VariantPricesBlock variantIndex={index} />

      <VariantBulkDiscountsBlock variantIndex={index} />

      <VariantBoughtTogetherBlock variantIndex={index} />
    </div>
  );
};

export default VariantBlock;
